import { useMemo } from 'react';

import { ConsumerStudentFee } from '@givebacks/api/student';
import { DateTime } from 'luxon';
import { Group, Stack, Text, ThemeIcon } from 'ui';
import { IconBank, IconCalendar, IconCash, IconReceipt, IconUser, UiIconsProps } from 'ui/Icons';

import { useStudentName } from '../../../../hooks/useStudentName';

type FeeDetailProps = {
  fee: ConsumerStudentFee;
};

const DetailGroup = ({
  Icon,
  label,
  value,
}: {
  Icon: React.JSXElementConstructor<UiIconsProps & { brand: 'tabler' }>;
  label: React.ReactNode;
  value: React.ReactNode;
}) => {
  return (
    <Group justify="space-between">
      <Group gap={8}>
        <ThemeIcon color="gray.6" variant="transparent" size={16}>
          <Icon brand="tabler" />
        </ThemeIcon>
        <Text c="gray.6">{label}</Text>
      </Group>
      <Text fw={500} fz="sm" c="gray.6">
        {value}
      </Text>
    </Group>
  );
};

const DueDate = ({ fee }: { fee: ConsumerStudentFee }) => {
  const dueDate = useMemo(() => {
    const dueAt = fee.due_at;
    if (!dueAt) return null;
    return DateTime.fromISO(dueAt);
  }, [fee.due_at]);

  const isDue = useMemo(() => {
    if (!dueDate) return false;
    return DateTime.now().diff(dueDate).toMillis() > 0;
  }, [dueDate]);

  if (!dueDate) return null;
  return (
    <DetailGroup
      label="Due Date"
      Icon={IconCalendar}
      value={<Text c={isDue ? 'cerise.6' : 'gray.8'}>{dueDate?.toFormat('MM/dd/yyyy')}</Text>}
    />
  );
};

const PartialPayment = ({ fee }: { fee: ConsumerStudentFee }) => {
  if (!fee.fee.allow_partial_payment) return null;
  return <DetailGroup label="Partial Payments" value="Accepted" Icon={IconCash} />;
};

const OptionalFee = ({ fee }: { fee: ConsumerStudentFee }) => {
  const label = useMemo(() => `${fee.fee.optional_label} Fee`, [fee.fee.optional_label]);
  if (!fee.fee.optional) return null;
  return <DetailGroup label={label} value="Yes" Icon={IconReceipt} />;
};

const ChargedTo = ({ fee }: { fee: ConsumerStudentFee }) => {
  const studentName = useStudentName(fee.student);
  return <DetailGroup label="Charged to" value={studentName} Icon={IconUser} />;
};

const School = ({ fee }: { fee: ConsumerStudentFee }) => {
  return <DetailGroup label="School" value={fee.cause.name} Icon={IconBank} />;
};

const FeeDetail = ({ fee }: FeeDetailProps) => {
  return (
    <Stack gap={8}>
      <DueDate fee={fee} />
      <PartialPayment fee={fee} />
      <OptionalFee fee={fee} />
      <ChargedTo fee={fee} />
      <School fee={fee} />
    </Stack>
  ); 
};

export { FeeDetail };
